import { createSelector } from "reselect";
import createCachedSelector from "re-reselect";
import { type SortDirection, type TabularColumnConfig, type TabularViewData } from "../models/models";
import { type SearchPageState } from "./types";
import { type FeatureData, type SearchResultRow } from "~/utils/models";
import { type RootState } from "~/app/store";
import { toMapFn } from "~/common/utils/reducers";
import { type ProductData } from "~/domains/product/types/ProductData";
import { convert, roundWeight, WeightUnit } from "../../../utils/WeightUtils";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { createDeepEqualSelector } from "./createDeepEqualSelector";
import { createTabularViewConfiguration } from "../utils/searchUtils";
import { UnitMode } from "~/domains/userPreferences/types/UnitMode";
import { NUMBER_OF_BOLTS_CODE } from "~/features/selectionAssistant/housing/constants";
import { isTechValueFilterCategory, type TechFilter } from "~/features/search/models/filterConfigModels";
import type { TabularColumnDefDTO } from "~/features/search/models/SearchResultsDTO";

const getTechValueFilters = (state: RootState, techValueTitle: string | string[]): TechFilter[] | undefined => {
  const config = selectActiveFilterConfiguration(state);
  if (config) {
    if (typeof techValueTitle === "string") {
      techValueTitle = [techValueTitle];
    }
    const validTitles = toMapFn(
      techValueTitle,
      (title) => title,
      () => true,
    );
    return config
      .filter(isTechValueFilterCategory)
      .flatMap((category) => category.items)
      .filter((filterCategory) => validTitles[filterCategory.title]);
  }
};

export const selectHasNonDefaultSort = (state: RootState) => {
  const { availableSorts, selectedCode } = state.srp.sort;
  return availableSorts[0] && selectedCode !== availableSorts[0].code;
};

export const selectBeSortCodeNested = (state: SearchPageState) => {
  const { selectedCode, selectedDirection } = state.sort;
  return getBESortCode(selectedCode, selectedDirection);
};

export const getBESortCode = (selectedCode: string | null, selectedDirection: SortDirection | null) => {
  if (!selectedCode) {
    return null;
  }
  if (selectedDirection) {
    return selectedCode + selectedDirection;
  }
  return selectedCode;
};

export const selectBeSortCode = (state: RootState) => selectBeSortCodeNested(state.srp);

export function selectActiveFilterConfiguration(state: RootState) {
  if (state.userPreferences.unitMode === UnitMode.Metric) {
    return state.srp.facets.filterConfig;
  } else {
    return state.srp.facets.filterConfigImperial;
  }
}

export const selectActiveTabularViewConfiguration = createCachedSelector<
  RootState,
  TabularColumnDefDTO[],
  TabularColumnConfig[]
>(
  // @ts-expect-error
  (state) => {
    if (state.userPreferences.unitMode === UnitMode.Metric) {
      return state.srp.config.tabularViewColConfig; //metric filter config
    } else {
      return state.srp.config.tabularViewColConfigImperial; //imperial filter config
    }
  },
  (tabularViewCols) => {
    if (!tabularViewCols) {
      return [];
    }
    return createTabularViewConfiguration(tabularViewCols);
  },
)((state, productGroupCode) => state.userPreferences.unitMode);

export function getProductGroupCodeForFilterConfig(fullProductGroupCode: string | undefined) {
  if (!fullProductGroupCode) {
    return "";
  }
  if (fullProductGroupCode.startsWith("/")) {
    fullProductGroupCode = fullProductGroupCode.slice(1);
  }
  return fullProductGroupCode.split("/")[0];
}

export const selectProductGroupCountMap = createSelector(
  (state: RootState) => state.srp.facets.facetValues["categoryPath"],
  (facetData) =>
    facetData
      ? facetData.values
          .filter((facetValue) => facetValue.count > 0)
          .reduce(
            (map, facetValue) => {
              map[facetValue.code] = facetValue.count;
              return map;
            },
            {} as Record<string, number>,
          )
      : {},
);

export const selectActiveProductGroups = (state: RootState) => state.srp.facets.selectedProductGroups;

export const selectFacetsWithResultsMap = createDeepEqualSelector(
  (state: RootState) => state.srp.facets.facetValues,
  (facetMap) =>
    Object.values(facetMap).reduce(
      (map, facet) => {
        map[facet.code] = true;
        return map;
      },
      {} as Record<string, boolean>,
    ),
);

export const selectProductResults = (state: RootState) => state.srp.results.results;
export const selectProductResultAmount = (state: RootState) => state.srp.results.pagination.totalNumberOfResults;
export const selectCurrentPage = (state: RootState) => state.srp.searchParams.currentPage;
export const selectProductPageAmount = (state: RootState) => state.srp.results.pagination.numberOfPages;

export const selectContentResults = (state: RootState) => state.srp.content.contentResults;
export const selectContentResultAmount = (state: RootState) => state.srp.content.contentPagination.totalNumberOfResults;
export const selectContentPageAmount = (state: RootState) => state.srp.content.contentPagination.numberOfPages;

export const selectIsInFlight = (state: RootState) => state.srp.meta.inFlight;
export const selectIsInit = (state: RootState) => state.srp.meta.init;

export const selectSearchQuery = (state: RootState) => state.srp.searchParams.text;
export const selectBreadcrumbs = (state: RootState) => state.srp.facets.breadcrumbs;

export const selectTechnicalInfo = (state: RootState) => state.srp.results.technicalInfo;

export const convertProductsToTabularViewData = (
  results: SearchResultRow[],
  productDict: Dictionary<ProductData>,
): TabularViewData[] => {
  const numberFormatter = NF.getFormatter();

  return results.map((row) => {
    const product = productDict[row.productCode];
    const base: TabularViewData = {
      id: row.id,
      weight: product.weight ? numberFormatter.format(roundWeight(convert(product.weight, WeightUnit.kg)).value) : "-",
      weightImperial: product.weightImperial
        ? numberFormatter.format(roundWeight(convert(product.weightImperial, WeightUnit.lbs)).value)
        : "-",
      product,
      variantProducts: row.variantProductCodes.map((code) => productDict[code]),
      techValues: toMapFn(row.features, (feature) => feature.techFilterId, makeTabularViewFeature),
    };
    if (product.numberOfBolts) {
      base.techValues[NUMBER_OF_BOLTS_CODE] = {
        value: product.numberOfBolts,
        abbreviation: "",
        isConverted: false,
      };
    }

    return base;
  });
};

function makeTabularViewFeature(feature: FeatureData) {
  let value;
  if (!feature.values.length) {
    value = "-";
  } else {
    value = feature.values.join(",\n");
  }
  return {
    value,
    abbreviation: feature.abbreviation ?? "",
    isConverted: feature.isConverted ?? false,
  };
}

export const selectTabularViewData = createSelector(
  selectProductResults,
  (state: RootState) => state.products.basic,
  (results, productMap) => {
    return convertProductsToTabularViewData(results, productMap);
  },
);

export const selectVisibleTabularViewColumns = createSelector(
  selectActiveTabularViewConfiguration,
  selectFacetsWithResultsMap,
  (colConfigList, visibilityMap) => {
    if (!colConfigList) {
      return null;
    }
    return colConfigList.filter((col) => visibilityMap[col.techValueUID]);
  },
);

export const selectIsFilterActive = (state: RootState) => state.srp.searchParams.showOnlyCampaignResults;
export const selectIsRelevantFilterActive = (state: RootState) => state.srp.searchParams.showOnlyRelevant;

// Additional selectors for nested state access
export const selectResultsPageSize = (state: RootState) => state.srp.results.pagination.pageSize;
export const selectContentPage = (state: RootState) => state.srp.content.contentPage;
export const selectAssistantValues = (state: RootState) => state.srp.facets.assistantValues;
export const selectFacetValueByCode = (state: RootState, code: string) => state.srp.facets.facetValues[code];
export const selectAppliedTechFilterValues = (state: RootState) => state.srp.facets.appliedTechFilterValues;
export const selectHasTubularResults = (state: RootState) => state.srp.results.hasTubularResults;
export const selectHasCampaignResults = (state: RootState) => state.srp.results.hasCampaignResults;
export const selectFirstResultProductCode = (state: RootState) => state.srp.results.firstResultProductCode;
export const selectProductsOnSale = (state: RootState) => state.srp.productsOnSale.productsOnSale;
export const selectProductsOnSaleInFlight = (state: RootState) => state.srp.productsOnSale.productsOnSaleInFlight;
export const selectFullTaxonomy = (state: RootState) => state.srp.config.fullTaxonomy;
export const selectIsProductFamilySelected = (state: RootState) => state.srp.facets.selectedProductGroups.length > 0;
